import React from 'react';
import { PageLayout } from '../components/layout/PageLayout';

export const TermsPage: React.FC = () => {
  return (
    <PageLayout
      title="Terms of Service"
      subtitle="Last updated: August 17, 2025"
    >
      <div className="space-y-8 text-slate-700 dark:text-slate-300">
        
        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
          <p className="mb-4">
            By accessing and using SleepCalc Pro ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service ("Terms") constitute a legally binding agreement between you and SleepCalc Pro ("we," "us," or "our").
          </p>
          <p>
            If you do not agree to abide by the above, please do not use this service. We reserve the right to update and change the Terms of Service from time to time without notice.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">2. Description of Service</h2>
          <p className="mb-4">
            SleepCalc Pro is a digital sleep optimization tool designed to help users improve their sleep quality through:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Sleep cycle calculations and recommendations</li>
            <li>Personalized sleep schedule optimization</li>
            <li>AI-powered sleep insights and recommendations</li>
            <li>Sleep pattern analysis and tracking</li>
            <li>Educational content about sleep health</li>
          </ul>
          <p>
            The Service is provided on an "as is" and "as available" basis. We do not warrant that the service will be uninterrupted or error-free.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">3. Medical Disclaimer</h2>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-4">
            <p className="font-semibold mb-2">IMPORTANT MEDICAL DISCLAIMER:</p>
            <p className="mb-2">
              SleepCalc Pro is NOT a medical device and is NOT intended to diagnose, treat, cure, or prevent any disease or medical condition. The information and recommendations provided by our service are for educational and informational purposes only.
            </p>
            <p>
              Always consult with a qualified healthcare professional before making any changes to your sleep routine, especially if you have sleep disorders, medical conditions, or are taking medications that affect sleep.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">4. User Accounts and Registration</h2>
          <p className="mb-4">
            To access certain features of the Service, you may be required to create an account. You are responsible for:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Maintaining the confidentiality of your account information</li>
            <li>All activities that occur under your account</li>
            <li>Providing accurate and truthful information</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>
          <p>
            You must be at least 13 years old to create an account. Users under 18 must have parental consent to use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">5. Subscription and Payment Terms</h2>
          <p className="mb-4">
            SleepCalc Pro offers both free and paid subscription tiers:
          </p>
          
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">5.1 Free Tier</h3>
          <p className="mb-4">
            The free tier provides basic sleep calculation features with limited functionality. No payment is required for free tier access.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">5.2 Pro Subscription</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Subscription fees are billed in advance on a monthly or annual basis</li>
            <li>Payment is processed through PayPal or other authorized payment processors</li>
            <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
            <li>Price changes will be communicated at least 30 days in advance</li>
            <li>No refunds for partial months of service</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">5.3 Cancellation</h3>
          <p className="mb-4">
            You may cancel your subscription at any time through your account settings or by contacting customer support. Cancellation will take effect at the end of your current billing period. You will retain access to Pro features until the subscription expires.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">6. Acceptable Use Policy</h2>
          <p className="mb-4">You agree not to use the Service to:</p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon intellectual property rights</li>
            <li>Transmit harmful, abusive, or offensive content</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use automated systems to access the Service without permission</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">7. Intellectual Property Rights</h2>
          <p className="mb-4">
            The Service and its original content, features, and functionality are and will remain the exclusive property of SleepCalc Pro and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used without our prior written consent.
          </p>
          <p>
            You retain ownership of any content you provide to the Service, but grant us a license to use, modify, and display such content for the purpose of providing the Service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">8. Privacy and Data Protection</h2>
          <p className="mb-4">
            Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to the collection and use of your information as described in the Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">9. Limitation of Liability</h2>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-4">
            <p className="mb-2">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL SLEEPCALC PRO BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATING TO THE SERVICE SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">10. Disclaimer of Warranties</h2>
          <p className="mb-4">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            We do not warrant that the Service will be uninterrupted, secure, or error-free, or that defects will be corrected.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">11. Indemnification</h2>
          <p className="mb-4">
            You agree to defend, indemnify, and hold harmless SleepCalc Pro and its affiliates from and against any claims, damages, costs, and expenses (including reasonable attorneys' fees) arising from or relating to your use of the Service or violation of these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">12. Termination</h2>
          <p className="mb-4">
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Service will cease immediately.
          </p>
          <p>
            You may terminate your account at any time by contacting customer support or through your account settings.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">13. Governing Law and Dispute Resolution</h2>
          <p className="mb-4">
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction where SleepCalc Pro is headquartered, without regard to conflict of law principles.
          </p>
          <p className="mb-4">
            Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">14. Changes to Terms</h2>
          <p className="mb-4">
            We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Your continued use of the Service after such changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">15. Severability</h2>
          <p className="mb-4">
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">16. Contact Information</h2>
          <p className="mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
            <p><strong>Email:</strong> legal@sleepcalc.pro</p>
            <p><strong>Address:</strong> [Company Address]</p>
            <p><strong>Phone:</strong> [Company Phone]</p>
          </div>
        </section>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-8 text-center text-sm text-slate-500 dark:text-slate-400">
          <p>
            By using SleepCalc Pro, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </div>
      </div>
    </PageLayout>
  );
};